(() => {
  const elementName = 'pm-home-tabs';

  if (customElements.get(elementName)) return;

  class PMHomeTabs extends HTMLElement {
    connectedCallback() {
      this.disconnectListeners();

      this.abortController = new AbortController();
      this.tabs = [...this.querySelectorAll('[data-pm-home-tabs-tab]')]
        .filter((tab) => tab.closest(elementName) === this);
      this.panels = [...this.querySelectorAll('[data-pm-home-tabs-panel]')]
        .filter((panel) => panel.closest(elementName) === this);

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.activate(tab.dataset.pmHomeTabsTab, true), {
          signal: this.abortController.signal
        });
        tab.addEventListener('keydown', (event) => this.handleKeydown(event), {
          signal: this.abortController.signal
        });
      });

      window.addEventListener('resize', () => this.syncActiveCanvas(), {
        signal: this.abortController.signal
      });

      document.addEventListener('shopify:section:load', (event) => {
        const eventTarget = event.target;
        if (
          eventTarget === this ||
          (eventTarget instanceof Node && (eventTarget.contains(this) || this.contains(eventTarget)))
        ) {
          this.reset();
        }
      }, { signal: this.abortController.signal });

      this.reset();
    }

    disconnectedCallback() {
      this.disconnectListeners();
    }

    disconnectListeners() {
      this.abortController?.abort();
      this.abortController = null;
    }

    reset() {
      this.activate('benefits', false);
    }

    activate(name, moveFocus) {
      const activeTab = this.tabs.find((tab) => tab.dataset.pmHomeTabsTab === name);
      const activePanel = this.panels.find((panel) => panel.dataset.pmHomeTabsPanel === name);
      if (!activeTab || !activePanel) return;

      this.dataset.activeTab = name;

      this.tabs.forEach((tab) => {
        const selected = tab === activeTab;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });

      this.panels.forEach((panel) => {
        panel.hidden = panel !== activePanel;
      });

      const lazyImage = activePanel.querySelector('[data-pm-home-tabs-lazy][data-src]');
      if (lazyImage) {
        lazyImage.src = lazyImage.dataset.src;
        lazyImage.removeAttribute('data-src');
      }

      this.syncCanvasHeight(activePanel);
      const activeImages = [...activePanel.querySelectorAll('img')];
      Promise.all(activeImages.map((image) => (
        typeof image.decode === 'function' ? image.decode().catch(() => {}) : Promise.resolve()
      )))
        .then(() => this.syncCanvasHeight(activePanel));
      document.fonts?.ready?.then(() => this.syncCanvasHeight(activePanel));

      if (moveFocus) activeTab.focus({ preventScroll: true });
    }

    syncActiveCanvas() {
      const activePanel = this.panels?.find((panel) => !panel.hidden);
      if (activePanel) this.syncCanvasHeight(activePanel);
    }

    syncCanvasHeight(activePanel) {
      const canvas = this.querySelector('.pm-home-tabs__canvas');
      if (!canvas || !activePanel) return;

      if (!window.matchMedia('(max-width: 1038px)').matches) {
        canvas.style.removeProperty('height');
        canvas.style.removeProperty('aspect-ratio');
        return;
      }

      requestAnimationFrame(() => {
        if (activePanel.hidden) return;
        const panelHeight = activePanel.getBoundingClientRect().height;
        canvas.style.height = `${Math.ceil(activePanel.offsetTop + panelHeight)}px`;
        canvas.style.aspectRatio = 'auto';
      });
    }

    moveRovingFocus(currentTab, key) {
      const currentIndex = this.tabs.indexOf(currentTab);
      if (currentIndex < 0) return;

      let targetIndex = currentIndex;
      if (key === 'ArrowRight') targetIndex = (currentIndex + 1) % this.tabs.length;
      if (key === 'ArrowLeft') targetIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
      if (key === 'Home') targetIndex = 0;
      if (key === 'End') targetIndex = this.tabs.length - 1;

      this.tabs.forEach((tab, index) => {
        tab.tabIndex = index === targetIndex ? 0 : -1;
      });
      this.tabs[targetIndex].focus({ preventScroll: true });
    }

    handleKeydown(event) {
      const tab = event.currentTarget;

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.activate(tab.dataset.pmHomeTabsTab, true);
        return;
      }

      if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      this.moveRovingFocus(tab, event.key);
    }
  }

  customElements.define(elementName, PMHomeTabs);
})();

/* Bank home benefit tiles (2026-10-08): one tile open at a time, so the grid
   never turns into a patchwork of half-flipped cards. Opening a tile closes the
   one that was open through its own control, so pm-flip-card keeps owning the
   flip and its aria-expanded state. */
(() => {
  if (document.documentElement.dataset.pkEntry !== 'returning') return;
  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('.pm-home-tabs__card pm-flip-card > button');
    if (!button) return;
    setTimeout(() => {
      if (button.getAttribute('aria-expanded') !== 'true') return;
      const group = button.closest('ul, ol') || document;
      group.querySelectorAll('.pm-home-tabs__card pm-flip-card > button[aria-expanded="true"]').forEach((open) => {
        if (open !== button) open.click();
      });
    }, 0);
  });
})();
